/**
 * Whether a modal overlay currently owns focus.
 *
 * The composer is refocused from several places — on session change and
 * reconnect, from the type-ahead redirect, and when the model picker or
 * context meter closes — none of which knows about a dialog. Each fires from
 * its own deferral (an animation frame, a `setTimeout(0)`), so once a modal is
 * open they can land AFTER the dialog has focused itself, putting the caret
 * behind the scrim while the dialog sits open in front of it.
 *
 * Keyed on the backdrop rather than `[role="dialog"]`: the backdrop is what
 * asserts "nothing behind me is interactive", and its presence exactly matches
 * the window during which that holds.
 */
export function aModalIsOpen(): boolean {
  return document.querySelector('[data-settings-backdrop]') != null;
}

/**
 * Hand the caret back to the composer after a footer control closes, so the
 * user can type straight after picking a model or reading the context
 * breakdown. Shared by `ModelPicker` and `ContextMeter`.
 *
 * Does nothing while a modal is open — on that path the control was dismissed
 * rather than used. Returns nothing: the composer is `disabled` while
 * disconnected, where `.focus()` is a silent no-op, so a caller wanting a
 * result has to check `document.activeElement` itself.
 */
export function restoreComposerFocus(): void {
  if (aModalIsOpen()) return;
  document
    .querySelector<HTMLTextAreaElement>('textarea[data-chat-input]')
    ?.focus();
}
